import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { apiClient } from "@/lib/api";

const formatTs = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString("tr-TR");
};

const formatNumber = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 8 });
};

const healthTone = (health) => {
  const normalized = String(health || "unknown").toLowerCase();
  if (normalized === "online") return "border-emerald-600 bg-emerald-950/20";
  if (normalized === "degraded") return "border-amber-600 bg-amber-950/20";
  return "border-slate-700 bg-slate-950/40";
};

const normalizeLabel = (value) => String(value || "")
  .trim()
  .toLowerCase()
  .replaceAll("_", " ")
  .replaceAll("-", " ")
  .replace(/\s+/g, " ");

const isInternalGeneratedProfile = (connection) => {
  const label = normalizeLabel(connection?.account_label);
  // Sadece sistem tarafından otomatik oluşturulan auto-seed profilleri filtrele.
  // Kullanıcının kendi oluşturduğu profiller (xilo_spot, xilo_futures, live_spot, live_futures vb.) asla filtrelenmez.
  return [
    "phase2 auto",
    "default binance spot live",
    "default binance futures live",
    "default binance spot testnet",
    "default binance futures testnet",
  ].some((token) => label === token || label.startsWith(`${token} `));
};

export const UserExchangeDiagnosticsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState([]);

  const loadConnections = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/user/exchange-connections");
      setConnections(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const grouped = useMemo(() => {
    const rawRows = Array.isArray(connections) ? connections : [];
    const cleanedRows = rawRows.filter((row) => !isInternalGeneratedProfile(row));
    const rows = cleanedRows.length > 0 ? cleanedRows : rawRows;
    const groupedMap = rows.reduce((acc, row) => {
      const env = String(row?.environment || "live").toLowerCase();
      const market = String(row?.market_type || "spot").toLowerCase();
      const exchange = String(row?.exchange || "binance").toLowerCase();
      const key = `${exchange}-${market}-${env}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(row);
      return acc;
    }, {});

    const picked = {};
    for (const [key, groupRows] of Object.entries(groupedMap)) {
      const ordered = [...groupRows].sort((left, right) => {
        const leftScore = Number(Boolean(left?.is_default)) * 10 + Number(Boolean(left?.can_trade_effective)) * 5 + (String(left?.connection_health || "") === "online" ? 3 : 0);
        const rightScore = Number(Boolean(right?.is_default)) * 10 + Number(Boolean(right?.can_trade_effective)) * 5 + (String(right?.connection_health || "") === "online" ? 3 : 0);
        if (rightScore !== leftScore) return rightScore - leftScore;
        return new Date(String(right?.updated_at || 0)).getTime() - new Date(String(left?.updated_at || 0)).getTime();
      });
      picked[key] = ordered[0] ? [ordered[0]] : [];
    }
    // Sıralama: live önce, sonra testnet; market_type spot önce, sonra futures
    const orderedEntries = Object.entries(picked).sort(([a], [b]) => {
      const [, aMarket, aEnv] = a.split("-");
      const [, bMarket, bEnv] = b.split("-");
      if (aEnv !== bEnv) return aEnv === "live" ? -1 : 1;
      return aMarket < bMarket ? -1 : 1;
    });
    return Object.fromEntries(orderedEntries);
  }, [connections]);

  return (
    <section className="space-y-4" data-testid="user-exchange-diagnostics-page">
      <header className="rounded border border-slate-800 bg-slate-900 p-4" data-testid="user-exchange-diagnostics-header">
        <h2 className="text-4xl font-black uppercase tracking-tight" data-testid="user-exchange-diagnostics-title">Bağlantı Durumu</h2>
        <p className="mt-2 text-sm text-slate-400" data-testid="user-exchange-diagnostics-subtitle">
          Burada kayıtlı bağlantılarınızın son durumunu görürsünüz. Bu ekran yalnız görünürlük içindir.
        </p>
        <div className="mt-3" data-testid="user-exchange-diagnostics-refresh-row">
          <Button type="button" variant="outline" onClick={loadConnections} disabled={loading} data-testid="user-exchange-diagnostics-refresh-button">
            {loading ? "Yenileniyor..." : "Bağlantıları Yenile"}
          </Button>
          <Button type="button" onClick={() => navigate("/user/exchange-settings")} data-testid="user-exchange-diagnostics-open-settings-button">
            Exchange Settings'e Git
          </Button>
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-2" data-testid="user-exchange-diagnostics-market-panels">
        {Object.entries(grouped).map(([key, rows]) => {
          const [exchangeName, marketName, envName] = key.split("-");
          const marketLabel = marketName === "futures" ? "Futures" : "Spot";
          const envLabel = envName === "testnet" ? "Testnet · Test/Fake Para" : "Live · Canlı/Gerçek Para";
          const envBadgeClass = envName === "testnet"
            ? "bg-amber-500/10 text-amber-300 border border-amber-500/30"
            : "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30";
          return (
          <article key={key} className="rounded border border-slate-800 bg-slate-900 p-4" data-testid={`user-exchange-diagnostics-group-${key}`}>
            <div className="flex items-center justify-between gap-3" data-testid={`user-exchange-diagnostics-group-header-${key}`}>
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs uppercase tracking-widest text-slate-300 font-semibold" data-testid={`user-exchange-diagnostics-group-title-${key}`}>
                  {exchangeName.toUpperCase()} · {marketLabel}
                </p>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${envBadgeClass}`} data-testid={`user-exchange-diagnostics-group-env-badge-${key}`}>
                  {envLabel}
                </span>
              </div>
              <Button type="button" variant="outline" onClick={() => navigate("/user/exchange-settings")} data-testid={`user-exchange-diagnostics-group-open-settings-${key}`}>
                Ayarlara Git
              </Button>
            </div>
            <div className="mt-3 space-y-3" data-testid={`user-exchange-diagnostics-group-list-${key}`}>
              {rows.map((connection) => (
                <div key={connection.id} className={`rounded border p-3 ${healthTone(connection.connection_health)}`} data-testid={`user-exchange-diagnostics-connection-${connection.id}`}>
                  {(() => {
                    const snapshot = connection.readiness_snapshot || {};
                    const available = snapshot.available_balance ?? connection.available_balance ?? null;
                    const wallet = snapshot.wallet_balance ?? snapshot.total_wallet_balance ?? connection.wallet_equity ?? null;
                    const pnl = snapshot.unrealized_pnl ?? null;
                    return (
                      <>
                  <p className="text-sm font-semibold text-slate-100" data-testid={`user-exchange-diagnostics-connection-label-${connection.id}`}>{connection.account_label}</p>
                  <p className="mt-1 text-xs text-slate-300" data-testid={`user-exchange-diagnostics-connection-health-${connection.id}`}>durum: {connection.connection_health || "unknown"}</p>
                  <p className="text-xs text-slate-400" data-testid={`user-exchange-diagnostics-connection-trade-${connection.id}`}>işleme uygun: {connection.can_trade_effective ? "evet" : "izleniyor"}</p>
                  <p className="text-xs text-slate-400" data-testid={`user-exchange-diagnostics-connection-available-${connection.id}`}>kullanılabilir bakiye: {formatNumber(available)}</p>
                  <p className="text-xs text-slate-400" data-testid={`user-exchange-diagnostics-connection-wallet-${connection.id}`}>cüzdan bakiyesi: {formatNumber(wallet)}</p>
                  <p className="text-xs text-slate-400" data-testid={`user-exchange-diagnostics-connection-pnl-${connection.id}`}>unrealized pnl: {formatNumber(pnl)}</p>
                  <p className="text-xs text-slate-400" data-testid={`user-exchange-diagnostics-connection-updated-${connection.id}`}>son güncelleme: {formatTs(connection.updated_at)}</p>
                  <p className="text-xs text-slate-400" data-testid={`user-exchange-diagnostics-connection-note-${connection.id}`}>{connection.connection_health_reason || connection.action_required_message || "Bağlantı notu yok."}</p>
                  <p className="mt-2 text-[11px] text-slate-500" data-testid={`user-exchange-diagnostics-connection-simplified-note-${connection.id}`}>Sade görünüm: yalnızca aktif/öncelikli bağlantı gösterilir. Tüm bağlantılar için Exchange Settings ekranını kullanın.</p>
                      </>
                    );
                  })()}
                </div>
              ))}
            </div>
          </article>
          );
        })}
        {Object.keys(grouped).length === 0 && <p className="text-sm text-slate-400" data-testid="user-exchange-diagnostics-empty">Henüz kayıtlı bağlantı yok.</p>}
      </div>
    </section>
  );
};